import gql from "graphql-tag";
import {useCallback} from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {
    aiGuideClaimSwagMutation,
    aiGuideClaimSwagMutationVariables,
} from "./__graphql-types__/aiGuideClaimSwagMutation.ts";
import type {aiGuideClaimSwagMutationType} from "./__graphql-types__/index.ts";

const AI_GUIDE_CLAIM_SWAG_MUTATION: GqlOperation<
    aiGuideClaimSwagMutationType["response"],
    aiGuideClaimSwagMutationType["variables"]
> = gqlOp(gql`
    mutation aiGuideClaimSwagMutation($kaid: String!, $swagId: String!) {
        claimAiGuideSwag(kaid: $kaid, swagID: $swagId) {
            error {
                code
            }
        }
    }
`);

/**
 * Returns a mutation function responsible for redeeming a guide fashion item.
 */
export const useRedeemGuideClosetItemMutation = (): [
    (variables: aiGuideClaimSwagMutationVariables) => Promise<void>,
    Result<aiGuideClaimSwagMutation>["status"] | null,
] => {
    const [mutate, result] = useMutation(AI_GUIDE_CLAIM_SWAG_MUTATION);

    const claimItem = useCallback(
        async (variables: aiGuideClaimSwagMutationVariables) => {
            try {
                const result = await mutate(variables);

                if (result.status !== "success") {
                    throw new KAError(
                        "Failed to call aiGuideClaimSwagMutation",
                        Errors.GraphqlResponse,
                    );
                }

                const error = result.data?.claimAiGuideSwag?.error;
                if (error) {
                    throw new KAError(
                        "aiGuideClaimSwagMutation succeeded with a failure",
                        Errors.GraphqlResponse,
                        {
                            cause: error.code,
                        },
                    );
                }
            } catch (error) {
                // We handle the exception thrown from within the mutation success
                // handler.
                if (error instanceof KAError) {
                    throw error;
                }
                // Otherwise we throw a generic error.
                throw new KAError(
                    "Failed to call aiGuideClaimSwagMutation",
                    Errors.Internal,
                );
            }
        },
        [mutate],
    );

    return [claimItem, result?.status ?? null];
};
